import {Injectable} from '@angular/core';
import {calculateRoundingAmount, roundForCalculation, toDisplayNumber, formatMonetaryValue} from './utils';
import {TaxService} from './tax.service';
import {ExtendedOrder} from "../@pages/pos/pos-split-bill-dialog/pos-split-bill-dialog.component";

@Injectable({
  providedIn: 'root',
})
export class OrderService {
  constructor(private customTaxService: TaxService) {}

  calculateOrderValues(orders: ExtendedOrder[], orderLevelDiscount: number = 0) {
    let taxRates = this.customTaxService.getTaxRates();

    let quantity = 0;
    let subtotal = 0;
    let tax1Amount = 0;
    let tax2Amount = 0;
    let inclusiveTax = 0;
    let totalAmount = 0;
    let roundingAmount = 0;
    let netAmount = 0;
    let averageCost = 0;
    let discountAmount = 0; // Will accumulate individual order discounts
    let discountItemAmount = 0;
    let paymentSubtotalItem = 0;
    let paymentDiscountItem = 0;
    let paymentQtyItem = 0;
    let paymentTotalOrder = 0;
    let paymentTotalTax1 = 0;
    let paymentTotalTax2 = 0;
    let paymentInclusiveTax = 0;
    let paymentSubtotalOrder = 0;
    let paymentQtyOrder = 0;
    let paymentDiscountItemOrder = 0;
    
    // First pass: calculate all subtotals to know total for proportional discount distribution
    let totalSubtotalForDiscountDistribution = 0;
    for (let order of orders) {
      let orderItems = order.orderItems.filter((x) => !x.deleted);
      if (orderItems) {
        let _subtotal = orderItems.map((x) => x.subtotal).reduce((prev, curr) => prev + curr, 0);
        totalSubtotalForDiscountDistribution += _subtotal;
      }
    }

    for (let order of orders) {

      let orderItems = order.orderItems.filter((x) => !x.deleted);
      if (orderItems) {
        for (let orderItem of [...orderItems]) {

          let variantPrice = 0;
          if (!orderItem['overridePrice']) {
            if (orderItem.varianceModel) {
              for (let variant of orderItem.varianceModel) {
                for (let variantOption of variant.options) {
                  variantPrice += variantOption.price;
                }
              }
            }
          }

          let tax1Config = taxRates.tax1Config;
          orderItem.subtotal = roundForCalculation(orderItem.quantity * (+orderItem.price + variantPrice));

          // Calculate discounted subtotal first (discount before tax)
          const discountedSubtotal = orderItem.subtotal - (orderItem.discountAmount || 0);

          if (tax1Config && tax1Config.inclusive) {
            orderItem.tax1Amount = 0;
            // Calculate per-unit tax, then multiply by toBePaidQuantity
            // Use 4 decimal precision for calculations
            const unitPriceWithVariant = +orderItem.price + variantPrice;
            const unitInclusiveTax = unitPriceWithVariant / (1 + tax1Config.rate) * tax1Config.rate;
            orderItem.inclusiveTax = roundForCalculation(unitInclusiveTax * orderItem.toBePaidQuantity);

            paymentInclusiveTax = paymentInclusiveTax + orderItem.inclusiveTax;
          }else {
            // Calculate exclusive tax on discounted amount (tax after discount)
            orderItem.tax1Amount = roundForCalculation(discountedSubtotal * (taxRates.tax1Config?.rate ?? 0));
          }

          //Use for split bill and payment tax - calculate on discounted amount
          if (tax1Config && !tax1Config.inclusive) {
            const unitDiscountedPrice = ((+orderItem.price + variantPrice) - ((orderItem.discountAmount || 0) / orderItem.quantity));
            const unitTax1 = roundForCalculation(unitDiscountedPrice * (taxRates.tax1Config?.rate ?? 0));
            paymentTotalTax1 = paymentTotalTax1 + (unitTax1 * orderItem.toBePaidQuantity);
          } else {
            paymentTotalTax1 = paymentTotalTax1 + orderItem.inclusiveTax;
          }

          // Calculate tax2Amount on discounted amount (tax after discount)
          orderItem.tax2Amount = roundForCalculation(discountedSubtotal * (taxRates.tax2Config?.rate ?? 0));

          //Use for split bill and payment tax - calculate on discounted amount
          const unitDiscountedPrice = ((+orderItem.price + variantPrice) - ((orderItem.discountAmount || 0) / orderItem.quantity));
          const unitTax2 = roundForCalculation(unitDiscountedPrice * (taxRates.tax2Config?.rate ?? 0));
          paymentTotalTax2 = paymentTotalTax2 + (unitTax2 * orderItem.toBePaidQuantity);

          // Don't apply rounding at item level - will be applied to final total
          orderItem.total = formatMonetaryValue(orderItem.subtotal);
          orderItem.roundingAmount = 0; // No rounding at item level

          orderItem.netAmount = formatMonetaryValue(orderItem.total + orderItem.tax1Amount + orderItem.tax2Amount - orderItem.discountAmount);

          //Get Subtotal item with discount
          let oneItemNetAmountWithDiscount = +orderItem.netAmount / orderItem.quantity;
          orderItem.paidAmount = +(oneItemNetAmountWithDiscount * orderItem.toBePaidQuantity).toFixed(2);

          // Fix #4: Defensive accumulation - only accumulate the portion being paid
          // Calculate per-unit discount amount
          const perUnitDiscount = (orderItem.discountAmount || 0) / orderItem.quantity;
          paymentDiscountItem = paymentDiscountItem + (orderItem.toBePaidQuantity * perUnitDiscount);

          // Calculate subtotal for the portion being paid (defensive check)
          const perUnitSubtotal = orderItem.subtotal / orderItem.quantity;
          const itemSubtotalToPay = perUnitSubtotal * orderItem.toBePaidQuantity;
          paymentSubtotalItem = paymentSubtotalItem + itemSubtotalToPay;

          paymentQtyItem += orderItem.toBePaidQuantity;

          // Get Subtotal order without discount (already per-unit calculation)
          let oneItemNetAmount = +(orderItem.subtotal) / orderItem.quantity;
          paymentSubtotalOrder = paymentSubtotalOrder + +(oneItemNetAmount * orderItem.toBePaidQuantity).toFixed(2);
        }

        // Sum up individual item taxes without premature rounding
        let _tax1Amount = orderItems.reduce((a, b) => a + b.tax1Amount, 0);
        let _tax2Amount = orderItems.reduce((a, b) => a + b.tax2Amount, 0);
        let _inclusiveTax = orderItems.reduce((a, b) => a + b.inclusiveTax, 0);
        // No item-level rounding amounts since rounding is applied to final total
        let _roundingAmount = 0;
        let _subtotal = orderItems.map((x) => x.subtotal).reduce((prev, curr) => prev + curr, 0);
        let _averageCost = formatMonetaryValue(orderItems.reduce((a, b) => a + b.averageCost, 0));
        let _discountItemAmount = formatMonetaryValue(orderItems.reduce((a, b) => a + b.discountAmount, 0));
        let _quantity = orderItems.reduce((a, b) => a + b.quantity, 0);


        order.subtotal = _subtotal;
        
        // Apply proportional order-level discount if any
        let orderLevelDiscountForThisOrder = 0;
        if (orderLevelDiscount > 0 && totalSubtotalForDiscountDistribution > 0) {
          // Distribute order-level discount proportionally based on this order's subtotal
          orderLevelDiscountForThisOrder = (orderLevelDiscount * _subtotal) / totalSubtotalForDiscountDistribution;
          // Set order-level discount (replace any existing order-level discount)
          order.discountAmount = orderLevelDiscountForThisOrder;
        } else if (orderLevelDiscount === 0) {
          // Clear order-level discount if no discount applied
          order.discountAmount = 0;
        }
        
        // Calculate order-level discounted subtotal for tax calculation
        // order.discountAmount now includes the order-level discount, so we don't double-count
        const totalOrderDiscount = (order.discountAmount || 0);
        const orderDiscountedSubtotal = _subtotal - totalOrderDiscount;
        
        // Recalculate taxes on order-level discounted amount if any discount exists
        if (totalOrderDiscount > 0 && _subtotal > 0) {
          // Calculate proportional tax adjustment for total discount (item + order level)
          const taxAdjustmentRatio = orderDiscountedSubtotal / _subtotal;
          order.tax1Amount = roundForCalculation(_tax1Amount * taxAdjustmentRatio);
          order.tax2Amount = roundForCalculation(_tax2Amount * taxAdjustmentRatio);
          order.inclusiveTax = roundForCalculation(_inclusiveTax * taxAdjustmentRatio);
        } else {
          order.tax1Amount = _tax1Amount;
          order.tax2Amount = _tax2Amount;
          order.inclusiveTax = _inclusiveTax;
        }
        
        order.discountItemAmount = _discountItemAmount;
        order.totalAmount = formatMonetaryValue(
          order.subtotal +
          order.tax1Amount +
          order.tax2Amount -
          (order.discountItemAmount || 0) -
          (order.discountAmount || 0)
        );
        // Apply rounding to the calculated total amount, not accumulated item rounding
        order.roundingAmount = formatMonetaryValue(calculateRoundingAmount(order.totalAmount));
        order.netAmount = formatMonetaryValue(order.totalAmount + order.roundingAmount);
        order.averageCost = _averageCost;

        subtotal += order.subtotal;
        tax1Amount += order.tax1Amount;
        tax2Amount += order.tax2Amount;
        inclusiveTax += order.inclusiveTax;
        totalAmount += order.totalAmount;
        averageCost += order.averageCost;
        discountAmount += order.discountAmount;
        discountItemAmount += order.discountItemAmount;
        quantity += _quantity;

        paymentTotalOrder =
          paymentSubtotalOrder + paymentTotalTax1 + paymentTotalTax2 - discountAmount - paymentDiscountItem;
      }
    }


    // Apply rounding to the final grand total, ensure all amounts are properly formatted
    // FIX: Calculate rounding on summed components to avoid floating point accumulation errors
    // When accumulating order.totalAmount (which includes taxes), floating point errors can occur
    // Example: 7.21 + 90.21 = 97.41999... → rounds to 97.42 (WRONG)
    // Instead: (6.80 + 85.10) + (0.41 + 5.11) = 91.90 + 5.52 = 97.41 (CORRECT)
    // This matches what gets stored in backend: subtotal + taxes - discounts
    const amountBeforeRounding = subtotal + tax1Amount + tax2Amount - discountAmount - discountItemAmount;
    totalAmount = formatMonetaryValue(amountBeforeRounding);
    roundingAmount = formatMonetaryValue(calculateRoundingAmount(amountBeforeRounding));
    netAmount = formatMonetaryValue(amountBeforeRounding + roundingAmount);
    paymentTotalOrder = formatMonetaryValue(paymentTotalOrder + roundingAmount);

    paymentQtyOrder = paymentQtyOrder + paymentQtyItem;
    paymentDiscountItemOrder = paymentDiscountItemOrder + paymentDiscountItem;

    return {
      subtotal: formatMonetaryValue(subtotal),
      // Format tax amounts for consistent 2-decimal display
      tax1Amount: formatMonetaryValue(tax1Amount),
      tax2Amount: formatMonetaryValue(tax2Amount),
      inclusiveTax: formatMonetaryValue(inclusiveTax),
      totalAmount,
      roundingAmount,
      netAmount,
      averageCost: formatMonetaryValue(averageCost),
      discountAmount: formatMonetaryValue(discountAmount),
      discountItemAmount: formatMonetaryValue(discountItemAmount),
      quantity,
      paymentTotalOrder,
      paymentQtyOrder,
      paymentSubtotalItem: formatMonetaryValue(paymentSubtotalItem),
      paymentDiscountItemOrder: formatMonetaryValue(paymentDiscountItemOrder),
      paymentSubtotalOrder: formatMonetaryValue(paymentSubtotalOrder),
      // Format payment tax amounts for consistent 2-decimal display
      paymentTotalTax1: formatMonetaryValue(paymentTotalTax1),
      paymentTotalTax2: formatMonetaryValue(paymentTotalTax2),
      paymentInclusiveTax: formatMonetaryValue(paymentInclusiveTax),
    };
  }

  /**
   * Round amounts for API calls and backend storage
   * Uses 4 decimal places for calculations
   * Internal calculations should maintain 4 decimal precision
   */
  private roundForApi(value: number): number {
    return roundForCalculation(value);
  }
}
